import { GenericServices } from './generic.services';
import { AxiosPromise } from "axios";
import {
	Appointment,
	AppointmentComment,
	AppointmentException,
	AppointmentForUpdatingMultiResourceModel,
	IAppointmentComment,
	IAppointmentException,
	IAppointmentForUpdatingMultiResourceModel,
	IAppointmentModel,
	IAppointmentProperty,
	IAppointmentStatusParameterModel,
	IAppointmentType,
	IAppointmentTypeParameterModel,
	IEvent,
	IRecurrencePatternObject,
	RecurrencePatternObject,
	AppointmentEvent,
	SuperAppointmentSuperEvent,
	Color,
	AppointmentType,
	AppointmentSmartSuggestionModel,
	AgendaUserConfig,
} from '../models/agenda';
import { IAppointmentListing, ISuperAppointmentListing } from '../models/agenda/appointment-listing.model';
import { AppointmentParameterModel } from '../models/agenda/appointment-parameter.model';
import Uri from '../common/uri/agenda-uri.constants';
import CommonUri from '../common/uri/common-uri.constants';
import { IAppointmentStatus } from '../models/agenda/appointment-status.model';
import moment, { Moment } from 'moment';
import { ApiResponse } from '../models/app-configuration';
import { DataHelper } from '../utils/data-helper';
import RecurrenceEnum from '../common/agenda-recurrence.constants';
import {
	IPersonAutoComplete,
	IPropertyAutoComplete,
	PersonAutoComplete,
	PropertyAutoComplete
} from '../models/autocomplete';
import AgendaStoreTypes from '../store/agenda/agenda-types.store';
import { IAppointmentConfirmation } from "../models/agenda/appointment-confirmation";
import { GenericBaseParameter } from '../models/base-parameter';
import { TaskParameterModel } from '../models/task';
import { COLOR_NUMBER } from '../common/constants';
import { OpenAppointmentEditModel } from '../models/agenda/open-appointment.model';
import store from '../store';
import { AGENDA_SLOT_DURATION } from '../common/agenda.constant';

export class AgendaServices extends GenericServices {
	/**
	 *
	 */
	constructor() {
		super();
	}

	async getConfigs(): Promise<any> {
		return super.executeSelecting(undefined, Uri.configuration)
			.then((response: ApiResponse) => response.isSuccess
				? Promise.resolve(response.data)
				: Promise.reject(response.message))
			.catch(error => Promise.reject(error))
	}

	async initAppointment(appointment: IAppointmentModel, userId: number, type?: IAppointmentType, agendaAppConfig?: AgendaUserConfig): Promise<IAppointmentModel> {
		try {
			//WA-4546  Store default duration
			const defaultSlotDuration = moment(appointment.EndTime).diff(moment(appointment.StartTime), 'minutes')
			let slotDuration = 0;
			if (store.getters['agendaList/config']) {
				slotDuration = store.getters['agendaList/config'] ? defaultSlotDuration : 0;
				const res = await this.getConfigs()
				if (res && res.AgendaSlotDurationMinutes) {
					let durationObject = AGENDA_SLOT_DURATION.find(e => e.KeyValue === res.AgendaSlotDurationMinutes);
					if (durationObject && durationObject.label && !slotDuration) {
						slotDuration = durationObject.label;
					}
				}
			}

			let startTime: Moment;
			// @ts-ignore
			let endTime: Moment;
			if (appointment.StartTime === undefined) {
				const now = moment();
				const currentMinute = Math.ceil((now.get('m') + 1) / 10) * 10;
				const diff = currentMinute - now.get('m');
				startTime = now.add(diff, 'm');
				if (agendaAppConfig && agendaAppConfig.start) {
					const arr = agendaAppConfig.start.split(":");
					if (arr.length >= 2) {
						const configHour = Number(arr[0]);
						const configMinute = Number(arr[1]);
						const convertedMinutes = (hour: number, min: number) => hour * 60 + min;
						if (convertedMinutes(startTime.hour(), startTime.minute()) < convertedMinutes(configHour, configMinute)) {
							startTime.hours(configHour);
							startTime.minutes(configMinute);
						}
					}
				}
			} else startTime = moment(appointment.StartTime);
			let duration: number = type.Duration ? type.Duration : (defaultSlotDuration || slotDuration);
			if (appointment.EndTime && !type.Duration) {
				duration = moment(appointment.EndTime).diff(startTime, 'm');
			}
			// endTime = appointment.EndTime ? moment(appointment.EndTime) : startTime.add(type.Duration, 'm');
			endTime = appointment.EndTime ? moment(appointment.EndTime) : startTime.clone().add(type.Duration, 'm');
			appointment = new Appointment({
				StartTime: DataHelper.formatDateTime(startTime),
				EndTime: DataHelper.formatDateTime(startTime.add(duration, 'm')),
				Duration: duration,
				AppointmentStatusId: type.DefaultStatus,
				AppointmentTypeId: type.Id,
				ListOfPerson: [],
				IsRecurrence: false,
				ListOfPersonId: [],
				ListOfProperty: [],
				ListOfComment: [],
				ListOfPropertyId: [],
				ListOfUserId: userId ? [userId] : [],
				DefaultSlotDuration: defaultSlotDuration || slotDuration
			});

			return Promise.resolve(appointment);
		} catch (error) {
			return Promise.reject(error);
		}
	}

	initRecurrencePattern(appointment: IAppointmentModel): IRecurrencePatternObject {
		let startTime = appointment.StartTime ? appointment.StartTime.replace(/\s/g, "T") : null;
		let startTimeParse = startTime ? new Date(startTime) : new Date();
		return new RecurrencePatternObject({
			RecurrenceType: RecurrenceEnum.RecurrenceType.Daily.toString(),
			DayOfWeek: DataHelper.formatCurrentDay(),
			PatternStartDate: DataHelper.formatDate(moment(appointment.StartTime)),
			DailyRecurrenceMode: RecurrenceEnum.DailyRecurrenceMode.UseInterval,
			StartTime: DataHelper.formatTime(moment(appointment.StartTime)),
			EndTime: DataHelper.formatTime(moment(appointment.EndTime)),
			RecurrenceEndMode: RecurrenceEnum.RecurrenceEndMode.noEndDate,
			MonthOfYear: startTimeParse.getMonth(),
			DayOfMonth: startTimeParse.getDay(),
			OccurrenceInMonth: RecurrenceEnum.OccurrenceInMonth.Second
		});
	}

	getListOfAppointments(params: AppointmentParameterModel): AxiosPromise<IAppointmentListing[]> {
		if (!params['Condition']) {
			return super.executeSelecting(params, Uri.superAppointment)
				.then((response: ApiResponse) => {
					return response.isSuccess ? Promise.resolve(response.data) : Promise.reject(response.message);
				})
				.catch(error => Promise.reject(error))
		} else {
			return super.executeSelectingPost(params, Uri.search)
				.then((response: ApiResponse) => {
					return response.isSuccess ? Promise.resolve(response.data) : Promise.reject(response.message);
				})
				.catch(error => Promise.reject(error))
		}

		// return super.executeSelectingPost(params, Uri.search)
		// 	.then((response: ApiResponse) => {
		// 		return response.isSuccess ? Promise.resolve(response.data) : Promise.reject(response.message);
		// 	})
		// 	.catch(error => Promise.reject(error))
	}

	getAppointmentDetail(params: any): AxiosPromise<IAppointmentListing> {
		return super.executeSelecting(params, Uri.superAppointmentDetail)
			.then((response: ApiResponse) => {
				return response.isSuccess ? Promise.resolve(response.data) : Promise.reject(response.message);
			})
			.catch(error => Promise.reject(error))
	}

	getAppointmentById(appointment: IAppointmentModel, onlyOne: boolean) {
		let params: any = { id: appointment.Id };
		let path: string = Uri.detail;
		if (onlyOne
			&& !appointment.IsException
			&& appointment.IsRecurrence) {
			params.date = DataHelper.formatDate(moment(appointment.StartTime));
			path = Uri.detailRecurrence;
		} else if (!onlyOne && appointment.IsException) {
			params.id = appointment.MasterId
		}
		return super.executeSelectBy(params, path)
			.then((response: ApiResponse) => {
				if (response.isSuccess) {
					const appointment: IAppointmentModel = response.data;
					response.data.StartTime = DataHelper.removeTimeZone(appointment.StartTime);
					response.data.EndTime = DataHelper.removeTimeZone(appointment.EndTime);
					response.data.ListOfComment = response.data.ListOfComment.map((comment: IAppointmentComment) => {
						const keys = Object.keys(comment);
						if (keys.indexOf('ObjectId') === -1)
							comment.ObjectId = 0;
						if (keys.indexOf('PersonId') === -1)
							comment.PersonId = 0;
						return comment;
					});
					if (response.data.IsRecurrence && onlyOne)
						response.data['IsOnly'] = true;
					return Promise.resolve(response.data);
				}
				else return Promise.reject(response.message);
			})
			.catch(error => Promise.reject(error))
	}

	getBasicAppointmentById(id: number) {
		const path: string = Uri.basicAppointment.replace(':id', id.toString())
		return super.executeSelecting(undefined, path)
			.then((response: ApiResponse) => {
				return response.isSuccess ? Promise.resolve(response.data) : Promise.reject(response.message);
			})
			.catch(error => Promise.reject(error))
	}

	getListOfComments(appointment: IAppointmentModel, payload: { property?: IPropertyAutoComplete, person?: IPersonAutoComplete }) {
		try {
			const listOfComments = DataHelper.deepClone<IAppointmentComment[]>(appointment.ListOfComment) || [];
			const personId = payload.person === undefined ? 0 : payload.person.Id;
			const objectId = payload.property === undefined ? 0 : payload.property.Id;

			if (listOfComments.length === 1
				&& appointment.ListOfPerson.length <= 1
				&& appointment.ListOfProperty.length <= 1
			) {
				if (listOfComments[0].ObjectId === undefined || objectId !== 0)
					listOfComments[0].ObjectId = objectId;
				if (listOfComments[0].PersonId === undefined || personId !== 0)
					listOfComments[0].PersonId = personId;
				if (listOfComments[0].ObjectId && listOfComments[0].PersonId) {
					listOfComments[0].NegativeFeedbackPoints = null;
					listOfComments[0].PositiveFeedbackPoints = null;
				}
			}
			else {
				const comment = new AppointmentComment('', { NegativeFeedbackPoints: '', PositiveFeedbackPoints: '' });
				comment.ObjectId = objectId;
				comment.PersonId = personId;
				const maxValue = Math.max(...[appointment.ListOfPerson.length, appointment.ListOfProperty.length]);

				if (listOfComments.length < maxValue)
					listOfComments.push(comment);

				if (appointment.ListOfPersonId.length === 1) {
					listOfComments.map(comment => this.setPersonIdForComment(comment, appointment.ListOfPersonId[0]).next().value);
				}
				if (appointment.ListOfPropertyId.length === 1) {
					listOfComments.map(comment => this.setObjectIdForComment(comment, appointment.ListOfPropertyId[0]).next().value);
				}
			}

			return Promise.resolve(listOfComments);
		} catch (error) {
			return Promise.reject(error);
		}
	}

	getListOfAppointmentTypes(params: IAppointmentTypeParameterModel): AxiosPromise<IAppointmentType[]> {
		return super.executeSelecting(params, CommonUri.agendaType);
	}

	getListOfAppointmentStatus(params: IAppointmentStatusParameterModel): AxiosPromise<IAppointmentStatus[]> {
		return super.executeSelecting(params, CommonUri.agendaStatus);
	}

	getAppointmentPlace(appointment: IAppointmentModel, actionOn?: string) {

		const param: {
			ActionOn?: string,
			AppointmentPlace?: string,
			AppointmentTypeId?: number,
			UserIds?: number[],
			PersonIds?: number[],
			PropertyIds?: number[]
		} = {
			ActionOn: actionOn,
			AppointmentPlace: appointment.AppointmentPlace,
			AppointmentTypeId: appointment.AppointmentTypeId,
			UserIds: appointment.ListOfUserId || [],
			PersonIds: appointment.ListOfPersonId || [],
			PropertyIds: appointment.ListOfPropertyId || []
		};

		return super.executeSelectingPost(param, Uri.getSuggestPlace)
			.then((response: ApiResponse) => {
				if (response.isSuccess) {
					return Promise.resolve(response.data)
				} else Promise.reject(response.message)
			})
			.catch(error => Promise.reject(error));
	}

	setPropertyForAppointment(appointment: IAppointmentModel, property: PropertyAutoComplete): Promise<PropertyAutoComplete[]> {
		const listOfProperties = DataHelper.deepClone<IAppointmentProperty[]>(appointment.ListOfProperty);
		const isExisted = listOfProperties.findIndex(p => p.Id === property.Id);

		if (isExisted > -1) {
			return Promise.reject(property.Reference);
		} else {
			if (listOfProperties.length >= 1 && appointment.ListOfPerson.length > 1)
				return Promise.reject('Could not add more property')
			else listOfProperties.push(property)
			return Promise.resolve(listOfProperties)
		}
	}

	setPersonForAppointment(appointment: IAppointmentModel, person: IPersonAutoComplete): Promise<PersonAutoComplete[]> {
		const listOfPeople = DataHelper.deepClone<IPersonAutoComplete[]>(appointment.ListOfPerson);
		const indexOfPerson = listOfPeople.findIndex((p: IPersonAutoComplete) => p.Id === person.Id)
		if (indexOfPerson > -1)
			return Promise.reject(person.Reference)
		else {
			if (listOfPeople.length >= 1 && appointment.ListOfPropertyId.length > 1)
				return Promise.reject('Could not add more person')
			else listOfPeople.push(person)
			return Promise.resolve(listOfPeople)
		}
	}

	removeFromList<T>(source: T[], id: number) {
		if (id === undefined)
			return Promise.reject('Id could not be empty');
		return Promise.resolve(source.filter((item: T) => item['Id'] !== id));
	}

	removeComment(appointment: IAppointmentModel, payload: { type: string, id: number }) {
		let listOfComments = DataHelper.deepClone<IAppointmentComment[]>(appointment.ListOfComment);
		const maxValue = Math.max(...[appointment.ListOfPropertyId.length, appointment.ListOfPersonId.length]);


		if (payload.type === AgendaStoreTypes.LIST_OF_PEOPLE) {
			listOfComments = listOfComments
				.filter(comment => this.createCondition(listOfComments, maxValue, comment.PersonId !== payload.id).next().value || appointment.ListOfPersonId.length === 0)
				.map(comment => this.removeCommentFromSource(appointment.ListOfPersonId.length, comment, payload.id, 'PersonId').next().value) as Array<IAppointmentComment>

		} else {
			listOfComments = listOfComments
				.filter(comment => this.createCondition(listOfComments, maxValue, comment.ObjectId !== payload.id).next().value || appointment.ListOfPropertyId.length === 0)
				.map(comment => this.removeCommentFromSource(appointment.ListOfPropertyId.length, comment, payload.id, 'ObjectId').next().value) as Array<IAppointmentComment>
		}
		return Promise.resolve(listOfComments);
	}

	insertAppointment(appointment: IAppointmentModel, copiedAppointmentId?: number) {
		let path: string = Uri.default;

		if (appointment.MasterId > 0) {
			// path = Uri.exceptionsFull.replace(':masterid', appointment.MasterId.toString());
			appointment.IsRecurrence = false;
			appointment.MasterId = 0;
		}
		const obj = this.mergeAppointment(appointment).next().value;
		if (obj.IsRecurrence && !obj.IsException)
			obj.RecurrencePatternObject = appointment.RecurrencePatternObject as any;

		let data = { ...obj, CopiedAppointmentId: 0 };
		if (copiedAppointmentId) {
			data = { ...obj, CopiedAppointmentId: copiedAppointmentId };
		}
		return super.executeCreating(data, path)
			.then((response: ApiResponse) => {
				return response.isSuccess
					? Promise.resolve(response.data)
					: Promise.reject(response['msg']);
			})
			.catch(error => Promise.reject(error))
	}

	updateAppointment(originAppointment: IAppointmentModel, appointment: IAppointmentModel) {
		const clonedAppointment = DataHelper.deepClone<IAppointmentModel>(appointment);
		const clonedOriginAppointment = DataHelper.deepClone<IAppointmentModel>(originAppointment);
		if (!clonedAppointment.IsRecurrence) {
			delete clonedAppointment.RecurrencePatternObject;
		}

		delete clonedOriginAppointment.Duration;
		delete clonedOriginAppointment.ListOfPerson;
		delete clonedOriginAppointment.ListOfProperty;
		delete clonedAppointment.Duration;
		delete clonedAppointment.ListOfPerson;
		delete clonedAppointment.ListOfProperty;

		clonedAppointment.ListOfComment = this.handleComment(clonedAppointment.ListOfComment).next().value;
		if (clonedAppointment.PriceEstimates) {
			clonedAppointment.PriceEstimates = this.handlePriceEstimates(clonedAppointment.PriceEstimates).next().value;
		}

		let path: string = Uri.default;
		if (clonedOriginAppointment.IsException) {
			path = Uri.exceptions.replace(':masterid', clonedOriginAppointment.MasterId.toString())
		}
		return super.executeUpdating(clonedOriginAppointment.Id, clonedOriginAppointment, clonedAppointment, path)
			.then((response: ApiResponse) => {
				return response.isSuccess
					? Promise.resolve(response.data)
					: Promise.reject(response);
			})
	}

	updateAppointmentByDrop(event: IEvent, originAppointment: IAppointmentListing) {
		let payload = {
			Id: event.originId,
			StartTime: DataHelper.formatDateTime(event.start),
			EndTime: DataHelper.formatDateTime(event.end),
		}

		// Change appointment to another
		// if (event.listOfUsers.length === 1) {
		payload['ListOfUserId'] = event.listOfUserId
		// }

		const appointment = new Appointment(payload);

		const originAppointmentCompact = new Appointment({
			Id: originAppointment.Id,
			StartTime: originAppointment.StartTime,
			EndTime: originAppointment.EndTime,
		});

		const path: string = Uri.default;

		return super.executeUpdating(originAppointment.Id, originAppointmentCompact, appointment, path)
			.then((response: ApiResponse) => {
				return response.isSuccess ? Promise.resolve(response.data) : Promise.reject(response.message);
			})
			.catch(error => {
				return Promise.reject(error)
			})
	}

	updateAppointmentByResourceId(event: IEvent, resourceId: string): Promise<any> {
		const compactAppointment: IAppointmentForUpdatingMultiResourceModel = new AppointmentForUpdatingMultiResourceModel(resourceId, event, true);
		const path: string = Uri.updateResource.replace(':id', event.originId.toString())
		return super.executeCreating(compactAppointment, path)
			.then((response: ApiResponse) => {
				return response.isSuccess ? Promise.resolve(response.isSuccess) : Promise.reject(response.message)
			})
			.catch(error => {
				return Promise.reject(error)
			})
	}

	insertExceptionAppointment(event: IEvent, exceptionDate: Moment) {
		const startDate = DataHelper.formatDate(event.start);
		const originStartDate = DataHelper.formatDate(exceptionDate);

		if (startDate !== originStartDate) {
			return Promise.reject('Expcetion is only created if 2 start date are the same');
		}

		const exceptionAppointment: IAppointmentException = new AppointmentException(event, exceptionDate)

		return super.executeCreating(exceptionAppointment, Uri.exceptions.replace(':masterid', exceptionAppointment.MasterId.toString()))
			.then((response: ApiResponse) => {
				return response.isSuccess
					? Promise.resolve(response.data)
					: Promise.reject(response.message);
			})
			.catch(error => Promise.reject(error));
	}

	insertFullExceptionAppointment(appointment: IAppointmentModel) {
		const appmt = this.mergeAppointment(appointment).next().value;
		return super.executeCreating(appmt, Uri.exceptionsFull.replace(':masterid', appointment.Id.toString()))
			.then((response: ApiResponse) => {
				return response.isSuccess
					? Promise.resolve(response.data)
					: Promise.reject(response.message);
			})
			.catch(error => Promise.reject(error));
	}

	updateExceptionAppointment(event: IEvent, appointment: IAppointmentModel) {

		const orginAppointment: IAppointmentModel = new Appointment({
			StartTime: appointment.StartTime,
			EndTime: appointment.EndTime
		});

		const newAppointment: IAppointmentModel = new Appointment({
			StartTime: DataHelper.formatDateTime(event.start),
			EndTime: DataHelper.formatDateTime(event.end)
		});
		let path: string = Uri.default;
		if (event.isException)
			path = Uri.updateException.replace(':masterid', event.masterId.toString())

		return super.executeUpdating(event.originId, orginAppointment, newAppointment, path)
			.then((response: ApiResponse) => {
				return response.isSuccess ? Promise.resolve(response.data) : Promise.reject(response.message);
			})
			.catch(error => Promise.reject(error))
	}

	deleteAppointment(event: IEvent, resourceId?: number, date?: Moment, isOnly?: boolean) {

		let path: string = Uri.delete.replace(':id', event[isOnly ? 'originId' : 'masterId'].toString());
		let requestHolder: Promise<ApiResponse>;
		if (resourceId > 0 && !event.isRecurrence) {
			path = Uri.deleteNormal.replace(':id', event.originId.toString())
			requestHolder = super.executeDeletingWith({
				AppointmentId: event.originId,
				DeleteUserId: resourceId,
				IsOnlyUser: true
			}, path)

		} else {
			if (isOnly && event.isRecurrence && date && !event.isException) {
				path = Uri.deleteRecurrence
					.replace(':id', event.originId.toString())
					.replace(':date', DataHelper.formatDate(date))
			}
			requestHolder = super.executeDeleting(path);
		}

		return requestHolder
			.then((response: ApiResponse) => {
				return response.isSuccess ? Promise.resolve(response.isSuccess) : Promise.reject(response.message)
			})
			.catch(error => Promise.reject(error))
	}

	getAgendaPriceEstimate(payload: { personId?: number, propertyId?: number }) {
		if (!payload.propertyId || !payload.personId) return Promise.reject(false);
		return super.executeSelectingNotEncode(payload, Uri.getAgendaPriceEstimate).then((response: ApiResponse) => {
			if (response.isSuccess) return response.data;
		})
	}

	getAgendaOptimizeFilterUsers(payload: { userids: string }) {
		return super.executeSelectingNotEncode(payload, Uri.getAgendaOptimizeFilterUsers).then((response: ApiResponse) => {
			if (response.isSuccess) return response.data;
		});
	}

	getOneAgenda<TValue, TResult>(
		param: TValue,
		path?: string
	): AxiosPromise<TResult> {
		return this.executeSelecting(param, path);
	}

	getAppointmentContextMenu(param: any) {
		return this.executePost(param, Uri.getAppointmentContextMenu)
	}

	postAppointmentConfirmation(payload: any) {
		return this.executePost(payload, Uri.postAppointmentConfirmation.replace(':agendaId', payload.Id));
	}

	postAppointmentNewConfirmation(payload: any) {
		return this.executePost(payload, Uri.postAppointmentNewConfirmation.replace(':agendaId', payload.Id));
	}

	postAppointmentGenerateConfirmation(payload: any) {
		return this.executePost(payload, Uri.postAppointmentGenerateConfirmation.replace(':agendaId', payload.Id));
	}

	postAppointmentInitialConfirmation(payload: any) {
		return this.executePost(payload, Uri.postAppointmentInitialConfirmation.replace(':agendaId', payload.Id));
	}

	postAppointmentNewMailConfirmation(payload: { params: any, Id: any }) {
		return this.executePost(payload.params.Confirmation, Uri.postAppointmentNewMailConfirmation.replace(':agendaId', payload.Id));
	}

	postAppointmentEditMailConfirmation(payload: { params: any, Id: any }, cancelTokenSource?: any) {
		return this.executeSelectingPost(payload.params, Uri.postAppointmentEditMailConfirmation.replace(':agendaId', payload.Id), cancelTokenSource);
	}

	postAppointmentNewSMSConfirmation(payload: { params: any, Id: any }) {
		return this.executePost(payload.params.Confirmation, Uri.postAppointmentNewSMSConfirmation.replace(':agendaId', payload.Id));
	}

	postAppointmentEditSMSConfirmation(payload: { params: any, Id: any }, cancelTokenSource?: any) {
		return this.executeSelectingPost(payload.params, Uri.postAppointmentEditSMSConfirmation.replace(':agendaId', payload.Id), cancelTokenSource);
	}

	postAppointmentSaveConfirmation(payload: { params: IAppointmentConfirmation, Id: any }) {
		return this.executePost(payload.params.Confirmations, Uri.postAppointmentSaveConfirmation.replace(':agendaId', payload.Id));
	}

	postAppointmentNewSaveConfirmation(payload: { params: IAppointmentConfirmation, Id: any }) {
		return this.executePost(payload.params.Confirmations, Uri.postAppointmentNewSaveConfirmation.replace(':agendaId', payload.Id));
	}

	postChangeStatusAppointment(payload: { params: { AppointmentId: number, NewStatusId: number, InternalComment: string }, Id: any }) {
		return this.executePost(payload.params, Uri.postChangeStatusAppointment.replace(':agendaId', payload.Id));
	}

	getNextStatusAppointment(payload: { params: {}, Id: any, Code: string }) {
		return this.executeSelecting(undefined, Uri.getNextStatusAppointment.replace(':code', payload.Code).replace(':agendaId', payload.Id));
	}

	getNextStatusesAppointment(payload: { params: {}, Id: any, Code: string }) {
		return this.executeSelecting(undefined, Uri.getNextStatusesAppointment.replace(':code', payload.Code).replace(':agendaId', payload.Id));
	}

	getListTasks(payload: { param: GenericBaseParameter<TaskParameterModel> }) {
		return this.executePost(payload, Uri.agendaGetTasks, true).then((response: ApiResponse) => {
			if (!response.isSuccess) { return Promise.reject(response); }
			return Promise.resolve(response.data);
		}).catch((error) => {
			return Promise.reject(error);
		});
	}

	postAgendaCreateFromTask(payload: { TaskId: number; UserId: number, StartTime: string }) {
		return this.executePost(payload, Uri.postAgendaCreateFromTask.replace(':taskId', payload.TaskId.toString()), true).then((response: ApiResponse) => {
			if (!response.isSuccess) { return Promise.reject(response); }
			return Promise.resolve(response.data);
		}).catch((error) => {
			return Promise.reject(error);
		});
	}

	postAgendaLoadFromTask(payload: { TaskId: number }) {
		return this.executeSelecting(payload, Uri.postAgendaLoadFromTask.replace(':taskId', payload.TaskId.toString()), true).then((response: ApiResponse) => {
			if (!response.isSuccess) { return Promise.reject(response); }
			return Promise.resolve(response.data);
		}).catch((error) => {
			return Promise.reject(error);
		});
	}

	private * setObjectIdForComment(comment: IAppointmentComment, id?: number) {
		comment.ObjectId = id;
		yield comment;
	}

	private * setPersonIdForComment(comment: IAppointmentComment, id?: number) {
		comment.PersonId = id;
		yield comment;
	}

	* mapEventFromAppointmentListingObject(appointment: IAppointmentListing, index: number) {
		let event = new AppointmentEvent(appointment, index);
		event.calculateDecrement();
		event.calculateAllDay();
		let statusColor = event.statusInfo !== undefined && event.statusInfo.Color != null ? event.statusInfo.Color : COLOR_NUMBER.TRANSPARENT
		event.setColor(new Color(!Color.isEmpty(statusColor)
			? statusColor
			: event.typeInfo !== undefined && event.typeInfo.Color != null ? event.typeInfo.Color : COLOR_NUMBER.TRANSPARENT));

		yield event;
	}

	* mapSuperEventFromSuperAppointmentListingObject(appointment: ISuperAppointmentListing, index: number) {
		let event = new SuperAppointmentSuperEvent(appointment, index);
		yield event;
	}

	* mapAppointmentTypeFromAppointmentListingObject(appointmentType: IAppointmentType) {
		yield new AppointmentType(appointmentType);
	}

	private * removeCommentFromSource(length: number, comment: IAppointmentComment, id: number, type: string) {
		if (length === 0) {
			comment[type] = 0;
		}
		else {
			if (comment[type] === id) {
				comment[type] = undefined;
			}
		}
		yield comment;
	}

	private * createCondition(listOfComments, maxValue, condition) {
		return (listOfComments.length > maxValue && condition)
			|| (listOfComments.length <= maxValue)
	}

	private * mergeAppointment(appointment: IAppointmentModel): any {
		let listOfComment = this.handleComment(appointment.ListOfComment).next().value;
		yield new Appointment({
			ListOfPersonId: appointment.ListOfPersonId,
			ListOfPropertyId: appointment.ListOfPropertyId,
			StartTime: DataHelper.formatDateTime(moment(appointment.StartTime)),
			EndTime: DataHelper.formatDateTime(moment(appointment.EndTime)),
			ExcludeFromReport: appointment.ExcludeFromReport,
			AlarmDuration: appointment.AlarmDuration || 0,
			Alarm: appointment.Alarm,
			ListOfComment: listOfComment,
			Subject: appointment.Subject,
			AppointmentPlace: appointment.AppointmentPlace,
			InternalComments: appointment.InternalComments,
			AppointmentStatusId: appointment.AppointmentStatusId,
			AppointmentTypeId: appointment.AppointmentTypeId,
			ListOfUserId: appointment.ListOfUserId,
			IsRecurrence: appointment.IsRecurrence,
			IsException: appointment.IsException,
			MasterId: appointment.MasterId
		})
	}

	// private reCalculateComments(appointment: IAppointmentModel) {
	// 	if (appointment.ListOfPersonId.length > appointment.ListOfPropertyId.length
	// 		&& appointment.ListOfPropertyId.length > 0) {
	// 		appointment.ListOfComment.forEach(comment => {
	// 			comment.ObjectId = appointment.ListOfPropertyId[0];
	// 		});
	// 	}
	// 	else {
	// 		if (appointment.ListOfPersonId.length > 0) {
	// 			appointment.ListOfComment.forEach(comment => {
	// 				comment.PersonId = appointment.ListOfPersonId[0];
	// 			});
	// 		}
	// 	}
	// }

	private * handleComment(listOfComment) {
		yield listOfComment.filter(comment => {
			if (comment && comment.Comment && comment.Comment.trim().length > 0) return comment;
			if (comment && comment.PositiveFeedbackPoints && comment.PositiveFeedbackPoints.trim().length > 0) return comment;
			if (comment && comment.NegativeFeedbackPoints && comment.NegativeFeedbackPoints.trim().length > 0) return comment;
			if (comment && comment.Id && comment.PriceEstimate > -1) return comment;
			if (comment && comment.PriceEstimate > 0) return comment;
			return false;
		});
	}

	private * handlePriceEstimates(priceEstimates) {
		yield priceEstimates.filter(item => {
			if (item.hasOwnProperty("Price"))
				return item.Price != null;
			return true
		});
	}

	initOpenAppointment() {
		return super.executeSelecting(undefined, Uri.initOpenAppointment)
			.then((response: ApiResponse) => {
				return response.isSuccess ? Promise.resolve(response.data) : Promise.reject(response.message);
			})
			.catch(error => Promise.reject(error))
	}
	createOpenAppointment(params: OpenAppointmentEditModel) {
		return super.executePost(params, Uri.createOpenAppointment)
			.then((response: ApiResponse) => {
				return response.isSuccess ? Promise.resolve(response.data) : Promise.reject(response.message);
			})
			.catch(error => Promise.reject(error))
	}

	deleteOpenAppointment(propertyId: number) {
		return super.executePost({
			PropertyId: propertyId
		}, Uri.deleteOpenAppointment)
			.then((response: ApiResponse) => {
				return response.isSuccess ? Promise.resolve(response.data) : Promise.reject(response.message);
			})
			.catch(error => Promise.reject(error))
	}

	getListOfCriterias(param: AppointmentSmartSuggestionModel) {
		return super
			.executeSelectingPost(param, Uri.smartSuggestion)
			.then((response: ApiResponse) => {
				if (response.isSuccess) {
					return Promise.resolve(response.data);
				}
				return Promise.reject(response.message);
			})
			.catch(ex => {
				console.log(ex);
				return Promise.reject(ex);
			});
	}

	populateToken2Condition<TResult>(entity: GenericBaseParameter<AppointmentParameterModel>, categoryType?: string): AxiosPromise<TResult> {
		return this.executeCreatingWithParam<GenericBaseParameter<AppointmentParameterModel>, any, any>(entity, { actionOn: categoryType || 'map' }, Uri.token2Condition)
			.then((response: ApiResponse) => {
				return response.isSuccess ? Promise.resolve(response.data) : Promise.reject(response.message);
			}).catch((err => Promise.reject(err)));
	}
}
